/**
 * Today — the two things a teammate comes here to do, first and biggest: upload the DataPack and
 * download the D-Plan. Then the one sentence that says what to do next, and Run again after a failure.
 *
 * **The sentence is `states[0].says`, and the console has no other source for it.** Its tone is the
 * server's `tone`; nothing here decides whether the day is ready (SPEC_5 Decision 2).
 *
 * **Nothing here moves when something happens.** The two buttons are the same size in every state:
 * Download is drawn disabled until there is a plan, and Upload says "Uploading…" in the same box. The
 * line under each button is a reserved slot that holds either its facts or the server's answer to the
 * last press, so an answer arriving never pushes the page down.
 *
 * The countdown is the one number this panel works out, from the heartbeat's `now` and the server's
 * `cutoff`, and it is display only: nothing branches on it.
 *
 * **Run again is a deliberate two-field act** (Decision 4): a required "why" and the button. It is
 * drawn only when `rerun.allowed`, i.e. after a failure; on every other day it isn't the next act.
 */

import { type ChangeEvent, type FormEvent, useRef, useState } from "react";
import { Failed, api, browserSays, newRequestId, upload, write } from "../../api/client";
import type { Today as TodayPayload } from "../../api/types/competition";
import { useCompetition } from "../../store/competition";
import { useT } from "../../i18n/useT";
import { saysIn } from "../../lib/says";
import { clock, dayLabel, span } from "../../lib/time";
import { Stamp } from "../ui/Stamp";
import { Info } from "../ui/Info";
import type { Lang } from "../../i18n/useT";

/** `Fri 23 Oct 2026`. */
export function longDay(day: string, lang: Lang): string {
  return `${dayLabel(day, lang)} ${day.slice(0, 4)}`;
}

type Said = { ok: boolean; says: string } | null;

export function Today({ today }: { today: TodayPayload }) {
  const { t, lang } = useT();
  const now = useCompetition((s) => s.heartbeat?.now);
  const hero = today.states[0];
  const left = now && today.cutoff ? (Date.parse(today.cutoff) - Date.parse(now)) / 1000 : null;

  return (
    <div className="today">
      {today.deadline && today.cutoff ? (
        <p className="today__deadline">
          {t("today.cutoff")} <b className="num">{clock(today.cutoff)}</b>
          <Info
            term={t("today.cutoff")}
            text={t("info.cutoff", { deadline: clock(today.deadline), cutoff: clock(today.cutoff) })}
          />
          {left !== null ? (
            <>
              {" · "}
              <b className="num" data-left={left > 0 ? "" : "passed"}>
                {left > 0 ? t("today.left", { span: span(left) }) : t("today.passed")}
              </b>
            </>
          ) : null}
        </p>
      ) : null}

      <div className="today__acts">
        <Upload today={today} />
        <Download today={today} />
      </div>

      {hero ? (
        <div className="hero" data-state={hero.name} data-level={hero.level} data-tone={hero.tone}>
          <p className="hero__says" data-says="">
            {saysIn(hero.says, lang)}
          </p>
        </div>
      ) : null}

      {today.rerun.allowed ? <Rerun /> : null}
    </div>
  );
}

/** What a failed write said, in one sentence: the server's when it answered, the browser's when not. */
function failure(error: unknown, act: string): string {
  if (!(error instanceof Failed)) throw error;
  const tr = error.trouble;
  return tr.kind === "refused" || tr.kind === "signed out"
    ? tr.refusal.says
    : tr.kind === "broke"
      ? tr.says
      : browserSays("post timed out", { act });
}

/** A hidden file input behind a real button (§4.2). The answer is the server's sentence, success or 422,
 *  shown in the slot under the button. */
function Upload({ today }: { today: TodayPayload }) {
  const { t } = useT();
  const load = useCompetition((s) => s.load);
  const input = useRef<HTMLInputElement>(null);
  const [said, setSaid] = useState<Said>(null);
  const [busy, setBusy] = useState(false);
  const dp = today.datapack;

  async function chosen(event: ChangeEvent<HTMLInputElement>) {
    const picked = event.currentTarget;
    const file = picked.files?.[0];
    if (!file) return;
    setBusy(true);
    setSaid(null);
    try {
      const answer = await upload<{ says: string }>("/datapack", file);
      setSaid({ ok: true, says: answer.says });
      await Promise.all([load("today"), load("portfolio")]);
    } catch (error) {
      setSaid({ ok: false, says: failure(error, t("up.act")) });
    } finally {
      setBusy(false);
      picked.value = ""; // the same file chosen again is a change too: "Same file …" is an answer
    }
  }

  return (
    <div className="bigact">
      <div className="bigact__row">
        <button type="button" className="btn bigact__btn" onClick={() => input.current?.click()} disabled={busy}>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 15V3" />
            <path d="M7 8l5-5 5 5" />
            <path d="M5 21h14" />
          </svg>
          {busy ? t("today.uploading") : t("up.button")}
        </button>
        <Info term="DataPack" text={t("info.datapack")} />
      </div>
      <input ref={input} className="vh" type="file" accept=".json,application/json" onChange={(e) => void chosen(e)} tabIndex={-1} />
      <p className="bigact__meta" data-upload-answer={said ? (said.ok ? "ok" : "refused") : undefined} data-tone={said && !said.ok ? "red" : undefined}>
        {said ? (
          said.says
        ) : dp ? (
          <>
            {t("up.uploaded")} <Stamp at={dp.uploaded_at} bare /> {t("up.by")} {dp.uploaded_by}
          </>
        ) : null}
      </p>
    </div>
  );
}

/** The D-Plan, fetched with the session's cookie so a 404's sentence can be shown instead of a browser
 *  error page. Drawn disabled until there is one, so the page keeps its shape. */
function Download({ today }: { today: TodayPayload }) {
  const { t } = useT();
  const plan = today.plan && today.trade_date ? today.plan : null;
  const [said, setSaid] = useState<string | null>(null);

  async function fetchPlan() {
    if (!plan) return;
    setSaid(null);
    try {
      const response = await fetch(api(`/plan/${today.trade_date}`), { credentials: "include" });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { says?: string };
        setSaid(body.says ?? `${response.status}`);
        return;
      }
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = plan.filename;
      a.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setSaid(browserSays("unreachable", { noun: plan.filename }));
    }
  }

  return (
    <div className="bigact">
      <div className="bigact__row">
        <button
          type="button"
          className="btn btn--accent bigact__btn"
          data-download=""
          data-sha256={plan?.sha256}
          disabled={!plan}
          onClick={() => void fetchPlan()}
        >
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v12" />
            <path d="M7 10l5 5 5-5" />
            <path d="M5 21h14" />
          </svg>
          {t("today.download")}
        </button>
        <Info term="D-Plan" text={t("info.dplan")} />
      </div>
      <p className="bigact__meta" data-download-says={said ? "" : undefined} data-tone={said ? "red" : undefined}>
        {said ?? (plan ? (
          <>
            <span className="mono">{plan.filename}</span> · {(plan.bytes_len / 1024).toFixed(1)} KB
          </>
        ) : null)}
      </p>
    </div>
  );
}

function Rerun() {
  const { t } = useT();
  const load = useCompetition((s) => s.load);
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<Said>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setSaid(null);
    try {
      const answer = await write<{ says: string }>("/rerun", { why: why.trim() }, newRequestId());
      setSaid({ ok: true, says: answer.says });
      setWhy("");
      await load("today");
    } catch (error) {
      setSaid({ ok: false, says: failure(error, t("today.rerun")) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="rerun" onSubmit={(e) => void submit(e)} data-allowed="">
      <label className="field rerun__why">
        <span className="lbl">{t("today.why")}</span>
        <input
          type="text"
          name="why"
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          required
          minLength={3}
          maxLength={300}
          disabled={busy}
        />
      </label>
      <button type="submit" className="btn" disabled={busy} data-rerun="">
        {t("today.rerun")}
      </button>
      <p className="bigact__meta rerun__said" data-rerun-answer={said ? (said.ok ? "ok" : "refused") : undefined} data-tone={said && !said.ok ? "red" : undefined}>
        {said?.says}
      </p>
    </form>
  );
}
